import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Footer from "@/components/Footer";
import { MountFade as FadeUp } from "@/components/motion";
import Seo, { SITE_URL } from "@/lib/seo";
import { fetchArticles, staticArticles } from "@/lib/articles";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });

export default function Insights() {
  const [articles, setArticles] = useState(staticArticles());
  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(() => {
    let on = true;
    fetchArticles().then((list) => on && setArticles(list));
    return () => { on = false; };
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Barrett Career Brand — Insights",
    url: `${SITE_URL}/insights`,
    blogPost: articles.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      datePublished: a.date,
      url: `${SITE_URL}/insights/${a.slug}`,
    })),
  };

  return (
    <main data-testid="insights-index" className="bg-[#0A0A0A] text-[#F8F8F5] min-h-screen">
      <Seo
        title="Career & CV Insights — Barrett Career Brand"
        description="Practical, no-fluff advice on CV writing, LinkedIn optimisation and career branding for Australian professionals, graduates and career changers."
        path="/insights"
        jsonLd={jsonLd}
      />

      <header className="border-b border-[#27272A]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
          <a href="/#consultation" className="font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors">Free consultation</a>
        </div>
      </header>

      <section className="max-w-[1200px] mx-auto px-6 pt-16 pb-8 md:pt-24">
        <FadeUp>
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Insights</p>
          <h1 className="font-display font-bold tracking-tighter leading-[1.0] text-4xl sm:text-5xl lg:text-6xl">
            Career advice that actually <span className="font-serif-accent font-light">moves the needle.</span>
          </h1>
          <p className="mt-6 font-body text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
            Practical thinking on CVs, LinkedIn and career branding — the same ideas I use with clients every day.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 pb-24">
        <div className="divide-y divide-[#27272A] border-t border-[#27272A]">
          {articles.map((a) => (
            <FadeUp key={a.slug}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className="group block py-10 grid md:grid-cols-12 gap-6 items-start"
              >
                <div className="md:col-span-3">
                  <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#DFFF00]">{a.category}</p>
                  <p className="mt-2 font-body text-sm text-[#52525B]">{fmtDate(a.date)} · {a.readingTime}</p>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[#F8F8F5] group-hover:text-[#DFFF00] transition-colors flex items-start gap-2">
                    {a.title}
                    <ArrowUpRight size={22} className="mt-1 shrink-0 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </h2>
                  <p className="mt-3 font-body text-[#A1A1AA] leading-relaxed max-w-2xl">{a.excerpt}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}
